import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Breakpoints, Colors, Index } from '@client/style/Variables';
import gsap from 'gsap';
import { useSearchQuery } from './QueryHook';
import SearchResults from './SearchResults';
import { TextStyle } from '@client/style/TextStyles';
import Functions from '@client/style/Functions';
import { groupByType, GroupedSearchResults } from './search-utils';
import Breaker from '@client/modules/Dividers/Breaker';
import Site from '@client/store/Site';
import Image from '@client/modules/Image';
import { trapFocus } from '@client/core/utils/a11y/trap-focus';

const FullScreenSearchWrapper = styled.section`
	display: flex;
	flex-direction: column;
	align-items: stretch;
	justify-content: start;
	position: fixed;
	width: 100vw;
	height: calc(100vh + var(--breaker-height) + 80px);
	top: 0;
	left: 0;
	z-index: ${Index.FullScreenMenu - 2};

	${Functions.breakpoint(Breakpoints.tablet)} {
		height: calc(100vh + var(--breaker-height) + 80px);
	}
`;

const FullScreenBackdrop = styled.section`
	position: fixed;
	width: 100vw;
	height: 100vh;
	z-index: ${Index.FullScreenMenu - 3};
	pointer-events: none;
	background-color: rgba(0, 0, 0, 0.8);

	${Functions.breakpoint(Breakpoints.tablet)} {
		background-color: rgba(0, 0, 0, 0);
		display: none;
	}
`;

const Content = styled.div<{ $color?: any; $desktopColor?: any }>`
	flex: 1 1 auto;
	display: flex;
	flex-direction: column;
	align-items: stretch;
	justify-content: start;
	background-color: ${props => props.$color?.color || '#ff9680'};
	color: ${props => props.$color?.contrastColor || Colors.lightText};
	overflow-y: auto;
	padding-bottom: 25vh;
	padding: 40px var(--gridMargin);
	padding-top: 180px;

	&:focus-visible {
		outline: none;
		> :first-child {
			outline: 2px auto;
		}
	}

	${Functions.breakpoint(Breakpoints.mobile)} {
		padding: 40px var(--gridGutter);
		padding-top: 120px;
	}

	${Functions.breakpoint(Breakpoints.tablet)} {
		padding: 80px var(--gridMargin);
		background-color: ${props => props.$desktopColor?.color || props.$color?.color || '#ff9680'};
		color: ${props => props.$desktopColor?.contrastColor || props.$color?.contrastColor || Colors.lightText};
	}
`;

const NoResultsContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	height: 100%;
	padding: 40px var(--gridMargin);
	gap: 20px;

	${Functions.breakpoint(Breakpoints.mobile)} {
		padding: 80px var(--gridMargin);
		gap: 40px;
	}
`;

const NoResultsImg = styled(Image)`
	width: 12rem;
	height: auto;
`;

const NoResultsTitle = styled.div`
	${TextStyle.family('title')}
	${TextStyle.weight('bold')}
	${TextStyle.size('p3', 'h6')}
	text-wrap: balance;
	text-align: center;
`;

const StyledBreaker = styled(Breaker)<{ $color?: any; $desktopColor?: any }>`
	height: var(--breaker-height);
	flex: 0 0 var(--breaker-height);
	width: 100%;

	path {
		fill: ${props => props.$color?.color || '#ff9680'};

		${Functions.breakpoint(Breakpoints.tablet)} {
			fill: ${props => props.$desktopColor?.color || props.$color?.color || '#ff9680'};
		}
	}
`;

export default function SearchContainer() {
	const [query] = useSearchQuery();
	const [results, setResults] = useState<GroupedSearchResults>([]);
	const [loading, setLoading] = useState(false);
	const wrapper = useRef<HTMLDivElement>(null);
	const backdrop = useRef<HTMLDivElement>(null);
	const content = useRef<HTMLDivElement>(null);

	const color = Site.use(data => data.search.color);
	const desktopColor = Site.use(data => data.search.desktopColor);
	const emptyResultsText = Site.use(data => data.search.emptyResultsText);
	const emptyResultsImage = Site.use(data => data.search.emptyResultsImage);

	useEffect(() => {
		if (query) {
			setLoading(true);
			fetch('/api/search?q=' + query)
				.then(response => response.json())
				.then(data => {
					const dataArr = data?.data ?? [];
					setResults(arr => (!dataArr.length && !arr.length ? arr : groupByType(dataArr)));
				})
				.catch(error => {
					console.error(error);
					setResults(arr => (arr.length ? [] : arr));
				})
				.finally(() => setLoading(false));
		} else {
			setResults(arr => (arr.length ? [] : arr));
		}
	}, [query]);

	useEffect(() => {
		let y: gsap.TweenValue = '-100%';
		let opacity: gsap.TweenValue = 0;
		if (query === '' || loading) {
			y = () => -window.innerHeight;
			opacity = 0.5;
		} else if (query !== undefined) {
			y = 0;
			opacity = 0.5;
		}

		gsap.to(backdrop.current, {
			duration: 0.4,
			ease: 'power2.inOut',
			opacity,
		});
		gsap.to(wrapper.current, {
			duration: 0.4,
			ease: 'power2.inOut',
			y,
		});
	}, [wrapper.current, backdrop.current, query, results, loading]);

	useEffect(() => {
		if (!content.current || !query || loading) {
			return;
		}

		const untrap = trapFocus(content.current);

		return () => {
			untrap?.();
		};
	}, [content.current, query, loading]);

	return (
		<>
			<FullScreenSearchWrapper ref={wrapper}>
				<Content
					$color={color}
					$desktopColor={desktopColor}
					aria-live='polite'
					aria-atomic='true'
					aria-busy={loading}
					id='site-search-content'
					tabIndex={query !== undefined ? 0 : -1}
					ref={content}
				>
					{results.length ? (
						<SearchResults data={results} />
					) : query && !loading ? (
						<NoResultsContainer>
							{emptyResultsImage ? <NoResultsImg data={emptyResultsImage} /> : null}
							<NoResultsTitle>{emptyResultsText || 'Ingen resultater'}</NoResultsTitle>
						</NoResultsContainer>
					) : null}
				</Content>
				<StyledBreaker flip={true} $color={color} $desktopColor={desktopColor} />
			</FullScreenSearchWrapper>
			<FullScreenBackdrop ref={backdrop} />
		</>
	);
}
